<template>
  <form
    @submit.prevent="submitBusinessCOAForm()"
  >
    <template v-if="!isVerifyInfo">
      <section class="py-8">
        <!-- Individual/Family Contact Information -->
        <div>
          <Tooltip
            placement="top"
            @show="analytics.click.tooltipInformation"
          >
            <h3
              v-if="isSimplify"
              ref="info-header"
              class="font-bold"
            >
              Mover Contact Information
            </h3>
            <h3
              v-else
              ref="info-header"
              class="font-bold"
            >
              What's your business name?
            </h3>
            <template
              slot="tip"
            >
              <p>
                Enter the primary business name in this field.
                <template v-if="flagBusinessAliasEnabled">
                  If you receive mail by more than one business name,
                  please include each business alias name in the expandable
                  fields.
                </template>
              </p>
              <p
                v-if="flagBusinessAliasEnabled"
                class="mt-3"
              >
                Each alias name added will be treated as its own Change-of-Address
                order. Only one email address will be collected and applied to all
                primary and alias names.
              </p>
            </template>
          </Tooltip>
          <div v-if="isVerifyEdit">
            <InputDropdown
              v-model="moverType"
              label="Who's Moving"
              name="mover-type"
              class="mt-4"
              :has-error="errors.moverType.hasError"
              :disabled="isVerifyEdit"
              @input="$event && analytics.click.moverType($event);"
            >
              <option
                value=""
                aria-describedby="mover-type-field"
              >
                Select
              </option>
              <option value="INDIVIDUAL">
                Individual
              </option>
              <option value="FAMILY">
                Family
              </option>
              <option value="BUSINESS">
                Business
              </option>
              <span
                v-if="errors.moverType.hasError"
                id="moverType-error"
                slot="helper"
                class="errors"
              >
                {{ errors.moverType.message }}
              </span>
            </InputDropdown>
            <template v-if="moverType === 'BUSINESS'">
              <div
                class="mt-6 flex flex-row"
                role="group"
                aria-labelledby="business-wholeCompany"
              >
                <p
                  id="business-wholeCompany"
                  class="flex-auto c-input-label"
                >
                  Is the ENTIRE business moving?
                </p>
                <InputRadio
                  v-model="businessWholeCompany"
                  form-value="YES"
                  name="business-whole-company"
                  class="flex-shrink"
                  :disabled="isVerifyEdit"
                  :has-error="errors.businessWholeCompany.hasError"
                  @input="businessWholeCompanyHandle('YES');
                          analytics.input.businessWholeCompany('YES');"
                >
                  Yes
                </InputRadio>
                <InputRadio
                  v-model="businessWholeCompany"
                  form-value="NO"
                  name="business-whole-company"
                  class="flex-shrink"
                  :disabled="isVerifyEdit"
                  :has-error="errors.businessWholeCompany.hasError"
                  @input="businessWholeCompanyHandle('NO');
                          analytics.input.businessWholeCompany('NO');"
                >
                  No
                </InputRadio>
              </div>
              <p
                :class="{ 'errors': errors.businessWholeCompany.hasError }"
                class="c-input-helper"
              >
                {{ errors.businessWholeCompany.message }}
              </p>
            </template>
          </div>
          <InputText
            ref="businessName"
            v-model.trim="businessName"
            label="Business Name"
            placeholder="Business Name"
            name="business-name"
            class="mt-5"
            :has-error="errors.businessName.hasError"
            @blur="analytics.input.businessName"
            @input="errors.businessName.hasError = false"
          >
            <span
              v-if="errors.businessName.hasError"
              slot="helper"
            >
              {{ errors.businessName.message }}
            </span>
          </InputText>
          <template v-if="flagBusinessAliasEnabled && showBusinessAlias">
            <div class="c-hairline mt-5 mb-5" />
            <BusinessAlias
              v-if="enhancedBusinessAliasesEnabled"
              ref="businessAliases"
              :value="businessAliases"
              :field-error="fieldErrors"
              :analytics="analytics"
              :business-name="businessName"
              :initial-alias-count="enhancedBusinessAliasesInitialCount"
              :aliases-increment="enhancedBusinessAliasesIncrementCount"
              :max-alias-count="enhancedBusinessAliasesMaxCount"
            />

            <template v-if="!enhancedBusinessAliasesEnabled">
              <InputText
                ref="businessAlias1"
                v-model.trim="businessAlias1"
                label="Business Alias 1"
                placeholder="Business Alias 1"
                name="business-alias-1"
                class="mt-5"
                :has-error="errors.businessAlias1.hasError"
                @blur="analytics.input.businessAlias('1')"
              >
                <span
                  v-if="errors.businessAlias1.hasError"
                  slot="helper"
                >
                  {{ errors.businessAlias1.message }}
                </span>
              </InputText>
              <InputText
                ref="businessAlias2"
                v-model.trim="businessAlias2"
                label="Business Alias 2"
                placeholder="Business Alias 2"
                name="business-alias-2"
                class="mt-5"
                :has-error="errors.businessAlias2.hasError"
                @blur="analytics.input.businessAlias('2')"
              >
                <span
                  v-if="errors.businessAlias2.hasError"
                  slot="helper"
                >
                  {{ errors.businessAlias2.message }}
                </span>
              </InputText>
              <InputText
                ref="businessAlias3"
                v-model.trim="businessAlias3"
                label="Business Alias 3"
                placeholder="Business Alias 3"
                name="business-alias-3"
                class="mt-5"
                :has-error="errors.businessAlias3.hasError"
                @blur="analytics.input.businessAlias('3')"
              >
                <span
                  v-if="errors.businessAlias3.hasError"
                  slot="helper"
                >
                  {{ errors.businessAlias3.message }}
                </span>
              </InputText>
              <InputText
                ref="businessAlias4"
                v-model.trim="businessAlias4"
                label="Business Alias 4"
                placeholder="Business Alias 4"
                name="business-alias-4"
                class="mt-5"
                :has-error="errors.businessAlias4.hasError"
                @blur="analytics.input.businessAlias('4')"
              >
                <span
                  v-if="errors.businessAlias4.hasError"
                  slot="helper"
                >
                  {{ errors.businessAlias4.message }}
                </span>
              </InputText>
            </template>
          </template>
          <div class="business-alias-buttons">
            <button
              v-if="flagBusinessAliasEnabled"
              id="biz-alias-button"
              ref="toggleBusinessAliases"
              type="button"
              class="text-blue-100 font-bold underline mt-5"
              @click="showBusinessAlias = !showBusinessAlias;
                      analytics.click.businessAliases(showBusinessAlias);"
            >
              <template v-if="showBusinessAlias">
                My business does not go by another name
              </template>
              <template v-else>
                My business also goes by another name
              </template>
            </button>
            <button
              v-if="showBusinessAlias && enhancedBusinessAliasesEnabled"
              id="biz-more-alias-button"
              type="button"
              class="biz-more-alias-button text-blue-100 font-bold underline mt-5"
              @click.prevent="moreAlias()"
            >
              <SvgPlusIcon
                color="#3573b1"
                :height="12"
                :width="12"
              /><span>Add more aliases</span>
            </button>
          </div>
        </div>
      </section>
      <section
        class="py-8 pb-0"
      >
        <div>
          <Tooltip
            placement="top"
            @show="analytics.click.tooltipOldAddress"
          >
            <h3
              v-if="isSimplify"
              class="font-bold"
            >
              Old Mailing Address
            </h3>
            <h3
              v-else
              class="font-bold"
            >
              What's your old address?
            </h3>
            <template slot="tip">
              <p>
                This is the address you're moving from. Enter a valid address,
                and remember to include apartment/suite number if applicable.
              </p>
              <p
                class="mt-5"
              >
                If your address is a PO Box, enter it in the Street Address
                field for example, PO Box 1001. Please note that Change-of-Address
                requests from non-USPS Private Mail Boxes (PMB) cannot be processed.
              </p>
              <p
                v-if="oldPoBoxException"
                class="errors text-red-200 mt-5"
              >
                <span class="font-bold">Note:</span> The address you provided is not
                considered a proper mailing address within this ZIP Code™. A proper
                mailing address within this ZIP Code™ requires a PO Box address.
                Please input your PO Box number in the “PO Box” field and click "Next"
                to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
              </p>
            </template>
          </Tooltip>
          <InputZipCode
            ref="oldZipCode"
            v-model="oldZipCode"
            label="ZIP Code™"
            placeholder="00000"
            name="old-zip-code"
            class="mt-5"
            :choice-experience="true"
            :has-error="errors.oldZipCode.hasError"
            :async-fn="zipSearch({
              cityName: oldCity,
              state: oldState,
            })"
            :transform-compare="zipCompare"
            :transform-label="zipLabel"
            :transform-value="zipValue"
            @blur="analytics.input.oldZipCode"
            @select="oldCity = $event.cityName;
                     oldState = $event.state"
          >
            <template slot="helper">
              {{ errors.oldZipCode.message }}
            </template>
          </InputZipCode>
          <InputListBox
            ref="oldCity"
            v-model="oldCity"
            label="City"
            placeholder="City"
            name="old-city"
            class="mt-5"
            :has-error="errors.oldCity.hasError"
            :async-fn="citySearch({
              zip: oldZipCode,
              state: oldState,
            })"
            :transform-compare="zipCompare"
            :transform-label="cityLabel"
            :transform-value="cityValue"
            @blur="analytics.input.oldCity"
            @select="oldZipCode = $event.zip; oldState = $event.state"
          >
            {{ errors.oldCity.message }}
          </InputListBox>
          <InputState
            ref="oldState"
            v-model="oldState"
            label="State"
            name="old-state"
            class="mt-5"
            :has-error="errors.oldState.hasError"
            @input="analytics.input.oldState()"
          >
            <span
              v-if="errors.oldState.hasError"
              slot="helper"
            >
              {{ errors.oldState.message }}
            </span>
          </InputState>
          <InputListBox
            ref="oldStreet"
            v-model="oldStreetAddress"
            label="Street Address"
            name="old-street-address"
            placeholder="Street Address"
            class="mt-5"
            :has-error="errors.oldStreetAddress.hasError && !showOldPoBox"
            :async-fn="streetSearch({
              cityName: oldCity,
              state: oldState,
              zipcode: oldZipCode,
            })"
            :transform-compare="streetCompare"
            :transform-label="streetLabel"
            :transform-value="streetValue"
            @input="oldPoBoxNumber = ''"
            @blur="analytics.input.oldStreet"
            @select="oldZipCode=$event.zip;
                     oldCity=$event.cityName;
                     oldState=$event.state;"
          >
            {{ errors.oldStreetAddress.message }}
          </InputListBox>
          <InputText
            v-if="oldState === 'PR'"
            ref="oldUrbanizationName"
            v-model="oldUrbanizationName"
            label="Urbanization Name (Puerto Rico only)"
            name="old-urbanization-name"
            placeholder="URB HIGHLAND GDNS"
            class="mt-5"
            :has-error="errors.oldUrbanizationName.hasError"
            @blur="analytics.input.oldUrbanizationName"
          >
            <span slot="helper">
              {{ errors.oldUrbanizationName.message }}
            </span>
          </InputText>
          <InputText
            v-if="showOldPoBox"
            ref="oldPoBox"
            v-model="oldPoBoxNumber"
            label="Po Box"
            name="old-po-box"
            placeholder="PO Box 123"
            class="mt-5"
            :has-error="errors.oldPoBoxNumber.hasError"
            @blur="analytics.input.oldPoBox"
            @input="oldAddressError = ''"
          >
            <span slot="helper">
              {{ errors.oldPoBoxNumber.message }}
            </span>
          </InputText>
        </div>
      </section>
      <div>
        <Tooltip
          v-if="!isVerifyEdit"
          placement="top"
          class="mt-3"
          @show="analytics.click.tooltipReuseOld"
        >
          <InputCheckbox
            v-model="reuseOld"
            name="reuse-old-address"
            class="c-input-checkbox--lg reuse-check"
            style="display: inline-block;"
          >
            Reuse this address
          </InputCheckbox>
          <template slot="tip">
            <p>
              Checking the reuse this address checkbox retains the address
              for future use. Unchecking the checkbox will erase the address
              when the save button is clicked.
            </p>
          </template>
        </Tooltip>
      </div>
      <section
        class="py-8 pb-0"
        :class="{ 'mt-8': isVerifyEdit }"
      >
        <div>
          <Tooltip
            placement="top"
            @show="analytics.click.tooltipNewAddress"
          >
            <h3
              v-if="isSimplify"
              class="font-bold"
            >
              New Mailing Address
            </h3>
            <h3
              v-else
              class="font-bold"
            >
              What's your new address?
            </h3>
            <template slot="tip">
              <p>
                This is the address you're moving to. Enter a valid address,
                and remember to include apartment/suite number if applicable.
              </p>
              <p
                class="mt-5"
              >
                If your address is a PO Box, enter it in the Street
                Address field for example, PO Box 1001. Please note
                that Change-of-Address request from non-USPS Private
                Mail Boxes (PMB) cannot be processed.
              </p>
              <p
                v-if="newPoBoxException"
                class="errors text-red-200 mt-5"
              >
                <span class="font-bold">Note:</span> The address you
                provided is not considered a proper mailing address
                within this ZIP Code™. A proper mailing address within
                this ZIP Code™ requires a PO Box address. Please input
                your PO Box number in the “PO Box” field and click "Next"
                to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
              </p>
            </template>
          </Tooltip>
          <InputZipCode
            ref="newZipCode"
            v-model="newZipCode"
            label="ZIP Code™"
            placeholder="00000"
            name="new-zip-code"
            class="mt-5"
            :choice-experience="true"
            :has-error="errors.newZipCode.hasError"
            :async-fn="zipSearch({
              cityName: newCity,
              state: newState,
            })"
            :transform-compare="zipCompare"
            :transform-label="zipLabel"
            :transform-value="zipValue"
            @blur="analytics.input.newZipCode"
            @select="newCity = $event.cityName;
                     newState = $event.state"
          >
            <template slot="helper">
              {{ errors.newZipCode.message }}
            </template>
          </InputZipCode>
          <InputListBox
            ref="newCity"
            v-model="newCity"
            label="City"
            placeholder="City"
            name="new-city"
            class="mt-5"
            :has-error="errors.newCity.hasError"
            :async-fn="citySearch({
              zip: newZipCode,
              state: newState,
            })"
            :transform-compare="zipCompare"
            :transform-label="cityLabel"
            :transform-value="cityValue"
            @blur="analytics.input.newCity"
            @select="newZipCode = $event.zip; newState = $event.state"
          >
            {{ errors.newCity.message }}
          </InputListBox>
          <InputState
            ref="newState"
            v-model="newState"
            label="State"
            name="new-state"
            class="mt-5"
            :has-error="errors.newState.hasError"
            @input="analytics.input.newState()"
          >
            <span
              v-if="errors.newState.hasError"
              slot="helper"
            >
              {{ errors.newState.message }}
            </span>
          </InputState>
          <InputListBox
            ref="newStreet"
            v-model="newStreetAddress"
            label="Street Address"
            name="new-street-address"
            placeholder="Street Address"
            class="mt-5"
            :has-error="errors.newStreetAddress.hasError && !showNewPoBox && !showNewPrivateMailbox"
            :async-fn="streetSearch({
              cityName: newCity,
              state: newState,
              zipcode: newZipCode,
            })"
            :transform-compare="streetCompare"
            :transform-label="streetLabel"
            :transform-value="streetValue"
            @input="newPoBoxNumber = ''; newPrivateMailbox = '';"
            @blur="analytics.input.newStreet"
            @select="newZipCode=$event.zip;
                     newCity=$event.cityName;
                     newState=$event.state;"
          >
            {{ errors.newStreetAddress.message }}
          </InputListBox>
          <InputText
            v-if="newState === 'PR'"
            ref="newUrbanizationName"
            v-model="newUrbanizationName"
            label="Urbanization Name (Puerto Rico only)"
            name="new-urbanization-name"
            placeholder="URB HIGHLAND GDNS"
            class="mt-5"
            :has-error="errors.newUrbanizationName.hasError"
            @blur="analytics.input.newUrbanizationName"
          >
            <span slot="helper">
              {{ errors.newUrbanizationName.message }}
            </span>
          </InputText>
          <InputText
            v-if="showNewPoBox"
            ref="newPoBox"
            v-model="newPoBoxNumber"
            label="Po Box"
            name="new-po-box"
            placeholder="PO Box 123"
            class="mt-5"
            :has-error="errors.newPoBoxNumber.hasError"
            @blur="analytics.input.newPoBox"
          >
            <template slot="helper">
              {{ errors.newPoBoxNumber.message }}
            </template>
          </InputText>
          <InputText
            v-if="showNewPrivateMailbox"
            ref="newPrivateMailbox"
            v-model="newPrivateMailbox"
            label="Private Mailbox"
            name="new-private-mailbox"
            placeholder="PMB 123"
            class="mt-5"
            :has-error="errors.newPrivateMailbox.hasError"
            @blur="analytics.input.newPrivateMailbox"
          >
            <template slot="helper">
              {{ errors.newPrivateMailbox.message }}
            </template>
          </InputText>
        </div>
      </section>
      <div>
        <Tooltip
          v-if="!isVerifyEdit"
          placement="top"
          class="mt-3"
          @show="analytics.click.tooltipReuseNew"
        >
          <InputCheckbox
            v-model="reuseNew"
            name="reuse-new-address"
            class="c-input-checkbox--lg reuse-check"
            style="display: inline-block;"
          >
            Reuse this address
          </InputCheckbox>
          <template slot="tip">
            <p>
              Checking the reuse this address checkbox retains the address
              for future use. Unchecking the checkbox will erase the address
              when the save button is clicked.
            </p>
          </template>
        </Tooltip>
      </div>
      <div
        v-if="isVerifyEdit"
        class="c-hairline mt-8"
      />
      <section
        v-if="!editMode && !isVerifyEdit"
        class="py-8"
      >
        <button
          id="biz-save"
          class="c-btn"
        >
          Save
        </button>
        <button
          v-if="(isSimplify || isChoice || isControl) && hasAtleastOneAddress"
          id="biz-add"
          class="c-btn third-button ml-4"
          @click.prevent="addNew()"
        >
          Add New
        </button>
      </section>
      <section
        v-else
        class="py-8"
      >
        <button
          id="biz-update"
          class="c-btn w-full"
        >
          Update
        </button>
        <div class="flex mt-6 justify-between multiBizButtons">
          <button
            class="c-btn--secondary w-1/2 p-0"
            :class="{'w-full': isVerifyEdit}"
            :disabled="primaryBusiness"
            @click.prevent="deleteBusinessCOA()"
          >
            Delete
          </button>
          <button
            v-if="isSimplify || isChoice || isControl"
            id="biz-add"
            class="c-btn w-1/2 p-0"
            @click.prevent="addNew()"
          >
            Add New
          </button>
        </div>
      </section>
    </template>
    <div v-if="(isVerifyInfo && hasMoreThanOneAddress) || isSimplify || isChoice || isControl">
      <div class="c-hairline" />
      <AddressList
        :is-verify-info="isVerifyInfo"
        :is-simplify="isSimplify"
        :mobile="true"
        :business="business"
        :bus-address="busAddresses.addresses||[]"
        :selected="orderId"
        @address-clicked="(orderId)=>editCOA(orderId)"
      />
    </div>
    <Modal
      ref="newConstructionModal"
      v-model="newConstructionModal"
      close-id="new-constr-close-x"
    >
      <ModalNewConstruction
        :street-address="newStreetAddress"
        :city="newCity"
        :state="newState"
        :zip-code="newZipCode"
        :urbanization-name="newUrbanizationName"
        @reset="newConstructionModal = false, newConstructionResetAttempts()"
        @override="businessNewConstructionOverride"
      />
    </Modal>
    <Modal
      ref="coaCheckModal"
      v-model="coaCheckModal"
    >
      <h4 class="font-bold">
        Based on your response...
      </h4>
      <p class="mt-3">
        A Change-of-Address already exists matching the name and address
        you've entered. To make changes to your current Change-of-Address,
        visit
        <a
          :class="['c-link--choice', 'c-link--inline']"
          :href="this.$sanitize($options.coacUrl)"
          rel="noopener noreferrer"
        >
          {{ $options.coacUrl }}
        </a>, enter your new ZIP Code™ and 16-digit confirmation code,
        which can be located within your confirmation email and the
        Confirmation Notification Letter you received at your new address.
      </p>
    </Modal>
  </form>
</template>

<script>
import {
  InputCheckbox,
  InputDropdown,
  InputListBox,
  InputRadio,
  InputState,
  InputText,
  InputZipCode,
  Tooltip,
  Modal,
} from '@mgo/ui/lib/components';

// mixin used in both Disclaimer/desktop.vue as well as MoveCombined/mobile.vue
import mixin from './mixin';
import addressViolations from '../../mixins/addressViolations';
import moverType from '../../mixins/moverType';
import newConstruction from '../../mixins/newConstruction';
import ModalNewConstruction from '../ModalNewConstruction.vue';
import rapidKey from '../../mixins/rapidKey';

import { desktopAnalytics } from '../../choice-views/MoveCombined/analytics';
import BusinessAlias from '../BusinessAlias.vue';
import AddressList from './AddressList.vue';
import SvgPlusIcon from '../SvgPlusIcon.vue';

export default {
  name: 'MultipleBusinessAddresses',
  components: {
    ModalNewConstruction,
    InputCheckbox,
    InputDropdown,
    InputListBox,
    InputRadio,
    InputState,
    InputText,
    InputZipCode,
    BusinessAlias,
    SvgPlusIcon,
    Tooltip,
    AddressList,
    Modal,
  },
  mixins: [
    addressViolations,
    mixin,
    moverType,
    newConstruction,
    rapidKey,
  ],
  data() {
    return {
      moverType: 'BUSINESS',
    };
  },
  computed: {
    analytics() {
      return desktopAnalytics();
    },
    hasMoreThanOneAddress() {
      return this.business?.length > 1;
    },
    hasAtleastOneAddress() {
      return this.business?.length > 0;
    },
  },
};
</script>
